import * as React from "react"
import { Button as UIButton, buttonVariants } from "@tmf/ui"
import { cn } from "@/utils/cn"

/**
 * Variantes APP-SPECIFIC do Tennis (cor `--court-accent`, definida em runtime por
 * tipo de quadra via themeStore). NÃO existem no `@tmf/ui` e não devem — este wrapper
 * fino COMPÕE o `<Button>` da lib aplicando só a cor de quadra. As demais variantes
 * (default/outline/ghost/...) e os recursos novos (loading/leftIcon/rightIcon/size touch)
 * vêm direto da lib.
 *
 * Nota: as props de variante são declaradas localmente (em vez de reusar
 * `VariantProps` do `@tmf/ui`) porque o tipo `ClassProp` do CVA é uma união que, ao
 * ser re-embrulhada em `forwardRef`, quebra a inferência contextual de handlers (onClick).
 */
type LibButtonVariant =
  | "default"
  | "destructive"
  | "outline"
  | "secondary"
  | "ghost"
  | "link"
type CourtVariant = "court" | "filled"
type ButtonSize = "default" | "sm" | "lg" | "touch" | "icon"

const courtClassName =
  "bg-court-accent text-white hover:bg-court-accent-hover hover:shadow-lg hover:shadow-court-accent/20"

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: LibButtonVariant | CourtVariant
  size?: ButtonSize
  asChild?: boolean
  /** Estado de carregamento: spinner à esquerda + interação desabilitada. */
  loading?: boolean
  /** Ícone à esquerda do conteúdo (substituído pelo spinner quando `loading`). */
  leftIcon?: React.ReactNode
  /** Ícone à direita do conteúdo. */
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, className, ...props }, ref) => {
    if (variant === "court" || variant === "filled") {
      return <UIButton ref={ref} className={cn(courtClassName, className)} {...props} />
    }
    return <UIButton ref={ref} variant={variant} className={className} {...props} />
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
